"use client";

import { Search, Star, PenLine } from "lucide-react";
import { motion } from "motion/react";

const HowItWorks = () => {
  const steps = [
    {
      icon: Search,
      title: "Search an Address",
      description: "Find out what other tenants are saying about any property.",
      color: "from-blue-50 to-blue-100",
      iconClass: "h-12 w-12 text-rentwise-blue-600",
    },
    {
      icon: Star,
      title: "Read Verified Reviews",
      description: "Get insights from real renters who've lived there.",
      color: "from-yellow-50 to-yellow-100",
      iconClass: "h-12 w-12 text-yellow-500",
    },
    {
      icon: PenLine,
      title: "Leave Your Own",
      description: "Share your experience to help future tenants.",
      color: "from-green-50 to-green-100",
      iconClass: "h-12 w-12 text-green-500",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut" as const
      }
    }
  };

  return (
    <div className="py-20 bg-gradient-to-b from-white to-rentwise-blue-50 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-rentwise-blue-100 rounded-full opacity-30 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-rentwise-blue-200 rounded-full opacity-30 blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            How It Works
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Get started in three simple steps and make informed rental decisions
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {steps.map((step, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group relative"
            >
              {/* Colored circle indicator */}
              <div className={`absolute -top-2 -left-2 w-6 h-6 rounded-full border-4 border-white shadow-lg ${
                index === 0 ? 'bg-blue-500' : 
                index === 1 ? 'bg-yellow-500' : 
                'bg-green-500'
              }`}></div>
              
              <div className={`
                relative rounded-2xl p-8 bg-gradient-to-br ${step.color}
                transform-gpu transition-all duration-300 group-hover:scale-105
                shadow-lg group-hover:shadow-xl border border-white/50
              `}>
                <div className="absolute inset-0 bg-white opacity-90 rounded-2xl transition-opacity group-hover:opacity-95"></div>
                <div className="relative flex flex-col items-center text-center">
                  <div className="mb-6 transform-gpu transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-1">
                    {/* Icon background circle with matching color */}
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                      index === 0 ? 'bg-blue-100 shadow-blue-200/50' : 
                      index === 1 ? 'bg-yellow-100 shadow-yellow-200/50' : 
                      'bg-green-100 shadow-green-200/50'
                    } shadow-lg`}>
                      {(() => { const IconComponent = step.icon; return <IconComponent className={step.iconClass} />; })()}
                    </div>
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">
                    {step.description}
                  </p>
                </div>
              </div>
              {/* Step number indicator */}
              <div className="absolute -top-4 -right-4 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-lg">
                {index + 1}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default HowItWorks;
